import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import { SkeletonRows, ErrorNotice } from '../ui/States';
import { fmt } from '../../lib/format';
import type { EventStats } from '../../lib/events';
import type { PanelProps } from './types';

/** Evaluation ratings from the session-feedback forms linked to the event. */
export default function FeedbackPanel({ detail }: PanelProps) {
  const stats = useQuery<EventStats>(
    ['event-stats', detail.event.id],
    async () => (await api.get(`/events/${detail.event.id}/stats`)).data.data,
  );
  if (stats.isLoading) return <SkeletonRows rows={4} />;
  if (stats.error || !stats.data) return <ErrorNotice>Could not load the evaluation.</ErrorNotice>;
  const r = stats.data.ratings;
  return (
    <Panel>
      <PanelHeader title="Feedback" note={`${fmt(r.responses)} response(s)`} />
      <PanelBody className="text-table">
        <ul>
          {r.numeric
            .filter((x) => x.answered > 0)
            .map((x) => (
              <li key={x.key} className="flex justify-between border-b border-line py-[6px]">
                <span>{x.label}</span>
                <span>{x.mean ?? '—'}</span>
              </li>
            ))}
        </ul>
        {r.eventRelevance.items.length ? (
          <div className="mt-4">
            <div className="text-small text-ink-2 mb-1">Relevance · {r.eventRelevance.answered} answered</div>
            <ul>
              {r.eventRelevance.items.map((x) => (
                <li key={x.value} className="flex justify-between border-b border-line py-[5px]">
                  <span>{x.value}</span>
                  <span>{fmt(x.count)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {r.responses ? null : (
          <p className="text-small text-ink-2 mt-3">Link the evaluation form in Before · Overview (event setup).</p>
        )}
      </PanelBody>
    </Panel>
  );
}
